/**
 * Shared reader for the centralized social/music profile source.
 *
 * Contract:
 *   • Single fetch of /api/social-profiles per app mount, cached in-module
 *     so Footer + Contact + Homepage section share ONE network call.
 *   • Refetches on window focus + on cross-tab admin update event.
 *   • NO hardcoded fallback: if the API is unavailable we return empty
 *     arrays and consumers hide their social section entirely.
 *   • Split by category for direct consumption.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CACHE_KEY = "__djspy_social_profiles_cache_v1";
const STALE_MS = 60_000;

let _memory = null;      // { music_platforms:[], social_media:[], fetchedAt:number }
let _inflight = null;    // Promise dedupe

// Subscribers for cross-component re-render on refresh.
const _subs = new Set();
function _notify() { _subs.forEach((cb) => { try { cb(_memory); } catch (_) {} }); }

async function _fetch(force = false) {
  const fresh = _memory && Date.now() - _memory.fetchedAt < STALE_MS;
  if (fresh && !force) return _memory;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/social-profiles");
      _memory = {
        music_platforms: Array.isArray(data?.music_platforms) ? data.music_platforms : [],
        social_media:    Array.isArray(data?.social_media)    ? data.social_media    : [],
        fetchedAt: Date.now(),
      };
    } catch (_e) {
      // No hardcoded fallback per user directive.  Empty on failure.
      _memory = { music_platforms: [], social_media: [], fetchedAt: Date.now() };
    } finally {
      _inflight = null;
    }
    _notify();
    return _memory;
  })();
  return _inflight;
}

export function refreshSocialProfiles() { return _fetch(true); }

export function useSocialProfiles() {
  const [state, setState] = useState(_memory || { music_platforms: [], social_media: [], fetchedAt: 0 });
  const [loading, setLoading] = useState(!_memory);

  const cb = useCallback((next) => { setState(next); setLoading(false); }, []);

  useEffect(() => {
    _subs.add(cb);
    _fetch(false).then(() => setLoading(false));
    const onFocus = () => _fetch(false);
    const onAdminUpdate = () => _fetch(true);
    window.addEventListener("focus", onFocus);
    window.addEventListener("djspy-social-profiles-updated", onAdminUpdate);
    return () => {
      _subs.delete(cb);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("djspy-social-profiles-updated", onAdminUpdate);
    };
  }, [cb]);

  return { ...state, loading };
}
